import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * Modal displaying the privacy policy.
 * @fires close - When the modal is dismissed.
 */
@customElement("bab-privacy-modal")
export class BabPrivacyModal extends LitElement {
  static styles = css`
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(2px);
      z-index: 2000;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
    }

    .modal-overlay--visible {
      opacity: 1;
      visibility: visible;
    }

    .modal-content {
      width: 90%;
      max-width: 560px;
      max-height: 80vh;
      background: var(--bab-card-bg);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      transform: scale(0.95) translateY(20px);
      transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .modal-overlay--visible .modal-content {
      transform: scale(1) translateY(0);
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: var(--bab-border-width, 1.5px) solid var(--bab-border);
      flex-shrink: 0;
    }

    .modal-header h3 {
      margin: 0;
      font-size: 1.1rem;
      color: var(--bab-text);
    }

    .modal-close {
      background: transparent;
      border: none;
      color: var(--bab-text-secondary);
      font-size: 1.25rem;
      cursor: pointer;
      padding: 4px;
      line-height: 1;
      transition: color 0.2s;
    }

    .modal-close:hover {
      color: var(--bab-text);
    }

    .modal-body {
      padding: 20px;
      overflow-y: auto;
    }

    .modal-body p {
      margin: 0 0 12px 0;
      font-size: 0.9rem;
      color: var(--bab-text-secondary);
      line-height: 1.5;
    }

    .modal-body h4 {
      margin: 20px 0 6px 0;
      font-size: 0.9rem;
      color: var(--bab-text);
    }

    .modal-body h4:first-child {
      margin-top: 0;
    }

    .modal-body ul {
      margin: 0 0 12px 0;
      padding-left: 20px;
      font-size: 0.9rem;
      color: var(--bab-text-secondary);
      line-height: 1.5;
    }
  `;

  @property({ type: Boolean })
  open = false;

  private _onKeyDown = (e: KeyboardEvent) => {
    if (!this.open) return;
    if (e.key === "Escape") {
      this._close();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("keydown", this._onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this._onKeyDown);
  }

  private _close() {
    this.dispatchEvent(new CustomEvent("close"));
  }

  render() {
    return html`
      <div class="modal-overlay ${this.open ? "modal-overlay--visible" : ""}"
           aria-hidden="${!this.open}"
           @click="${this._close}">
        <div class="modal-content"
             role="dialog"
             aria-modal="true"
             aria-labelledby="privacy-modal-title"
             @click="${(e: Event) => e.stopPropagation()}">
          <div class="modal-header">
            <h3 id="privacy-modal-title">Privacy Policy</h3>
            <button class="modal-close" aria-label="Close" @click="${this._close}">✕</button>
          </div>
          <div class="modal-body">
            <h4>What stays on your device</h4>
            <p>
              Your ratings, names, and colors live in the page URL and in your
              browser's local storage. Nothing is sent anywhere unless you
              explicitly choose to submit or share it.
            </p>

            <h4>Sharing a link</h4>
            <p>
              Copying the share link or downloading a chart image encodes your
              data directly into that URL or file. Anyone you send it to can
              read it — treat it like you would any document you hand out.
            </p>

            <h4>Submitting ratings</h4>
            <p>
              If you use "Submit Ratings," your ratings and any demographic
              answers you provide are sent to our server and stored so we can
              aggregate results. A random identifier is stored in your browser
              so repeat submissions can be recognized; it isn't tied to your
              name or any other identifying information.
            </p>

            <h4>What we don't do</h4>
            <ul>
              <li>We don't use tracking or analytics cookies.</li>
              <li>We don't sell or share data with third parties.</li>
              <li>We don't require an account or collect contact details.</li>
              <li>We don't share any individual data, only aggregate statistics.</li>
            </ul>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bab-privacy-modal": BabPrivacyModal;
  }
}
